import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../api';
import { useAuth } from '../context/AuthContext';

// Quotations: what customers asked for, what we offered, and whether they took
// it. Requests come in from WhatsApp (a customer messaging the quote keyword),
// from here, or through the API.

const STATUS_BADGE = {
  requested: 'badge-warning',
  quoted: 'badge-info',
  sent: 'badge-brand',
  accepted: 'badge-success',
  declined: 'badge-neutral',
  expired: 'badge-neutral',
};

const STATUS_LABEL = {
  requested: 'Needs pricing',
  quoted: 'Ready to send',
  sent: 'Sent',
  accepted: 'Accepted',
  declined: 'Declined',
  expired: 'Expired',
};

const SOURCE_LABEL = { whatsapp: 'WhatsApp', app: 'Created here', api: 'API' };

const money = (amount, currency = 'USD') => `${currency} ${Number(amount || 0).toFixed(2)}`;
const shortDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

export default function Quotations() {
  const { user } = useAuth();
  const isOwner = user?.team_role !== 'agent';
  const [data, setData] = useState({ quotations: [], total: 0, pages: 1, summary: {} });
  const [status, setStatus] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (status) params.status = status;
      if (query) params.q = query;
      const r = await api.get('/quotations', { params });
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not load quotations');
    } finally {
      setLoading(false);
    }
  }, [status, query, page]);

  useEffect(() => { load(); }, [load]);

  const send = async (quotation) => {
    if (!window.confirm(`Send ${quotation.number} to ${quotation.contact?.name || quotation.contact?.phone} on WhatsApp?`)) return;
    try {
      await api.post(`/quotations/${quotation._id}/send`);
      toast.success(`${quotation.number} sent`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send it', { duration: 7000 });
    }
  };

  const openPdf = async (quotation) => {
    try {
      const r = await api.get(`/quotations/${quotation._id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener');
      // Give the new tab a moment to take the blob before releasing it.
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error('Could not open the PDF'); }
  };

  const setStatusOf = async (quotation, next) => {
    try {
      await api.put(`/quotations/${quotation._id}`, { status: next });
      toast.success(`Marked ${next}`);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const summaryTiles = [
    { key: 'requested', label: 'Needs pricing' },
    { key: 'quoted', label: 'Ready to send' },
    { key: 'sent', label: 'Sent' },
    { key: 'accepted', label: 'Accepted' },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Quotations</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {isOwner && (
            <button className="btn btn-ghost" onClick={() => setShowSettings(true)}>⚙ Quoting settings</button>
          )}
          <button className="btn btn-primary" onClick={() => setCreating(true)}>+ New quotation</button>
        </div>
      </div>
      <p className="page-desc">
        Customers ask for prices on WhatsApp and get a PDF quotation back. You can also raise one
        here, or from your own systems through the API.
      </p>

      <div className="stat-grid">
        {summaryTiles.map(tile => (
          <div className="stat-card" key={tile.key}>
            <div className="stat-value">{data.summary?.[tile.key]?.count ?? 0}</div>
            <div className="stat-label">
              {tile.label}
              {data.summary?.[tile.key]?.value
                ? ` · ${money(data.summary[tile.key].value)}` : ''}
            </div>
          </div>
        ))}
      </div>

      <div className="card" style={{ padding: 20 }}>
        <div className="search-bar">
          <input className="input search-input" placeholder="Search by quote number…"
            value={query} onChange={e => { setPage(1); setQuery(e.target.value); }} />
          <select className="select" style={{ width: 180 }} value={status}
            onChange={e => { setPage(1); setStatus(e.target.value); }}>
            <option value="">All statuses</option>
            {Object.entries(STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="empty-state"><div className="skeleton skeleton-text" style={{ width: 240 }} /></div>
        ) : data.quotations.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon" aria-hidden="true">🧾</div>
            <div className="empty-state-title">No quotations yet</div>
            <p className="empty-state-desc">
              Turn on quoting in the settings and customers can request prices on WhatsApp — or
              raise one here for a customer who called.
            </p>
            <button className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>+ New quotation</button>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Quote</th><th>Customer</th><th>Status</th><th>Source</th>
                  <th className="num">Total</th><th>Valid until</th><th />
                </tr>
              </thead>
              <tbody>
                {data.quotations.map(q => (
                  <tr key={q._id}>
                    <td>
                      <button className="btn-link" style={{ padding: 0, fontWeight: 600 }}
                        onClick={() => setViewing(q)}>{q.number}</button>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        {q.items?.length || 0} line{q.items?.length === 1 ? '' : 's'}
                        {q.unmatched?.length ? ` · ${q.unmatched.length} unmatched` : ''}
                      </div>
                    </td>
                    <td>
                      {q.contact?.name || 'Unknown'}
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{q.contact?.phone}</div>
                    </td>
                    <td>
                      <span className={`badge badge-dot ${STATUS_BADGE[q.status] || 'badge-neutral'}`}>
                        {STATUS_LABEL[q.status] || q.status}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{SOURCE_LABEL[q.source] || q.source}</td>
                    <td className="num" style={{ fontWeight: 600 }}>{money(q.total, q.currency)}</td>
                    <td style={{ fontSize: 12 }}>{shortDate(q.valid_until)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => openPdf(q)}>PDF</button>
                        {['requested', 'quoted', 'sent'].includes(q.status) && (
                          <button className="btn btn-primary btn-sm" onClick={() => send(q)}>
                            {q.status === 'sent' ? 'Resend' : 'Send'}
                          </button>
                        )}
                        {q.status === 'sent' && (
                          <button className="btn btn-ghost btn-sm" onClick={() => setStatusOf(q, 'accepted')}>Accepted</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data.pages > 1 && (
          <div className="pagination">
            <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
            <span>Page {page} of {data.pages}</span>
            <button className="btn btn-ghost btn-sm" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        )}
      </div>

      {viewing && (
        <QuotationModal
          quotation={viewing}
          onClose={() => setViewing(null)}
          onChanged={() => { setViewing(null); load(); }}
          onSend={send}
          onPdf={openPdf}
        />
      )}
      {creating && (
        <NewQuotationModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />
      )}
      {showSettings && (
        <SettingsModal onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
}

function QuotationModal({ quotation, onClose, onChanged, onSend, onPdf }) {
  const [full, setFull] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/quotations/${quotation._id}`).then(r => {
      setFull(r.data);
      setNotes(r.data.notes || '');
    }).catch(() => setFull(quotation));
  }, [quotation]);

  const q = full || quotation;

  const saveNotes = async () => {
    setSaving(true);
    try {
      await api.put(`/quotations/${q._id}`, { notes });
      toast.success('Saved — the PDF will be rebuilt on the next send');
      onChanged();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 660 }}>
        <div className="card-header" style={{ marginBottom: 12 }}>
          <div>
            <div className="modal-title" style={{ marginBottom: 2 }}>{q.number}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {q.contact?.name || 'Unknown'} · {q.contact?.phone} · raised {shortDate(q.createdAt)}
            </div>
          </div>
          <span className={`badge badge-dot ${STATUS_BADGE[q.status] || 'badge-neutral'}`}>
            {STATUS_LABEL[q.status] || q.status}
          </span>
        </div>

        {q.warehouse && (
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
            Supplied from <strong>{q.warehouse.name}</strong> ({q.warehouse.code})
          </p>
        )}

        <div className="table-wrap" style={{ marginBottom: 12 }}>
          <table>
            <thead>
              <tr><th>Item</th><th className="num">Qty</th><th className="num">Unit price</th><th className="num">Total</th></tr>
            </thead>
            <tbody>
              {(q.items || []).map((item, i) => (
                <tr key={i}>
                  <td>
                    {item.name}
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {item.sku || 'ad-hoc'}
                      {item.discount_pct ? ` · ${item.discount_pct}% off` : ''}
                      {item.tax_rate ? ` · ${item.tax_rate}% tax` : ''}
                      {item.stock_at_quote !== null && item.stock_at_quote !== undefined
                        ? ` · ${item.stock_at_quote} in stock when quoted` : ''}
                    </div>
                  </td>
                  <td className="num">{item.quantity}{item.unit ? ` ${item.unit}` : ''}</td>
                  <td className="num">{money(item.unit_price, q.currency)}</td>
                  <td className="num">{money(item.line_total, q.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginLeft: 'auto', width: 260, fontSize: 13 }}>
          {[['Subtotal', q.subtotal], ...(q.discount_total ? [['Discount', -q.discount_total]] : []),
            ...(q.tax_total ? [['Tax', q.tax_total]] : [])].map(([label, value]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-muted)' }}>
              <span>{label}</span><span>{money(value, q.currency)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 15, marginTop: 6 }}>
            <span>Total</span><span>{money(q.total, q.currency)}</span>
          </div>
        </div>

        {q.unmatched?.length > 0 && (
          <div className="alert alert-warning" style={{ marginTop: 14 }}>
            <div>
              <div className="alert-title">Asked for but not quoted</div>
              {q.unmatched.join(' · ')} — these weren't in the catalog. They're printed on the PDF
              so the customer knows they still need confirming.
            </div>
          </div>
        )}

        {q.requested_text && (
          <div style={{ marginTop: 14 }}>
            <div className="label">What the customer wrote</div>
            <pre style={{
              background: 'var(--surface2)', padding: 10, borderRadius: 6, fontSize: 12,
              whiteSpace: 'pre-wrap', fontFamily: 'inherit', color: 'var(--text-muted)',
            }}>{q.requested_text}</pre>
          </div>
        )}

        <div className="form-group" style={{ marginTop: 14 }}>
          <label className="label">Notes on the quote</label>
          <textarea className="textarea" style={{ minHeight: 60 }} value={notes}
            onChange={e => setNotes(e.target.value)} placeholder="Delivery within 3 working days." />
          <span className="field-help">Printed on the PDF under the totals.</span>
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          <button className="btn btn-ghost" onClick={() => onPdf(q)}>Open PDF</button>
          {notes !== (q.notes || '') && (
            <button className={'btn btn-secondary' + (saving ? ' is-loading' : '')} onClick={saveNotes} disabled={saving}>
              Save notes
            </button>
          )}
          {['requested', 'quoted', 'sent'].includes(q.status) && (
            <button className="btn btn-primary" onClick={() => onSend(q)}>
              {q.status === 'sent' ? 'Resend on WhatsApp' : 'Send on WhatsApp'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function NewQuotationModal({ onClose, onCreated }) {
  const [contacts, setContacts] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [form, setForm] = useState({ contact: '', warehouse: '', notes: '' });
  const [lines, setLines] = useState([{ sku: '', quantity: 1, discount_pct: '' }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/contacts', { params: { limit: 200 } })
      .then(r => setContacts(r.data.contacts || r.data || []))
      .catch(() => {});
    api.get('/products', { params: { limit: 200, active: 'true' } })
      .then(r => { setProducts(r.data.products || []); setWarehouses(r.data.warehouses || []); })
      .catch(() => {});
  }, []);

  const setLine = (i, field, value) => setLines(ls => ls.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  const product = (sku) => products.find(p => p.sku === sku);

  const estimate = lines.reduce((sum, l) => {
    const p = product(l.sku);
    if (!p) return sum;
    const gross = (Number(l.quantity) || 0) * p.price;
    return sum + gross - gross * ((Number(l.discount_pct) || 0) / 100);
  }, 0);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = {
        contact: form.contact,
        warehouse: form.warehouse || undefined,
        notes: form.notes,
        items: lines
          .filter(l => l.sku && Number(l.quantity) > 0)
          .map(l => ({
            sku: l.sku,
            quantity: Number(l.quantity),
            discount_pct: Number(l.discount_pct) || 0,
          })),
      };
      if (!payload.items.length) throw new Error('Add at least one line');
      const r = await api.post('/quotations', payload);
      toast.success(`${r.data.number} created`);
      onCreated();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not create the quotation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 640 }}>
        <div className="modal-title">New quotation</div>

        {error && <div className="alert alert-danger" role="alert">{error}</div>}
        {products.length === 0 && (
          <div className="alert alert-info">
            <div>Add products to the Catalog first — a quotation is priced from your catalog.</div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label className="label">Customer <span className="required">*</span></label>
            <select className="select" value={form.contact} onChange={e => setForm(f => ({ ...f, contact: e.target.value }))}>
              <option value="">Choose a customer…</option>
              {contacts.map(c => (
                <option key={c._id} value={c._id}>{c.name || 'Unnamed'} — {c.phone}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="label">Supply from</label>
            <select className="select" value={form.warehouse} onChange={e => setForm(f => ({ ...f, warehouse: e.target.value }))}>
              <option value="">Default warehouse</option>
              {warehouses.map(w => <option key={w._id} value={w._id}>{w.name}</option>)}
            </select>
          </div>
        </div>

        <div className="label">Lines</div>
        <div className="table-wrap" style={{ marginBottom: 8 }}>
          <table>
            <thead>
              <tr><th>Product</th><th className="num">Qty</th><th className="num">Discount %</th><th className="num">Line</th><th /></tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const p = product(l.sku);
                const gross = p ? (Number(l.quantity) || 0) * p.price : 0;
                const net = gross - gross * ((Number(l.discount_pct) || 0) / 100);
                return (
                  <tr key={i}>
                    <td>
                      <select className="select" value={l.sku} onChange={e => setLine(i, 'sku', e.target.value)}>
                        <option value="">Choose…</option>
                        {products.map(prod => (
                          <option key={prod._id} value={prod.sku}>
                            {prod.name} — {money(prod.price, prod.currency)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td><input className="input" type="number" min="1" style={{ width: 80 }}
                      value={l.quantity} onChange={e => setLine(i, 'quantity', e.target.value)} /></td>
                    <td><input className="input" type="number" min="0" max="100" style={{ width: 80 }}
                      value={l.discount_pct} onChange={e => setLine(i, 'discount_pct', e.target.value)} /></td>
                    <td className="num">{p ? money(net, p.currency) : '—'}</td>
                    <td>
                      <button className="btn btn-ghost btn-sm" disabled={lines.length === 1}
                        onClick={() => setLines(ls => ls.filter((_, idx) => idx !== i))}>✕</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <button className="btn btn-ghost btn-sm"
          onClick={() => setLines(ls => [...ls, { sku: '', quantity: 1, discount_pct: '' }])}>+ Add line</button>

        <div className="form-group" style={{ marginTop: 14 }}>
          <label className="label">Notes <span className="optional">(optional)</span></label>
          <textarea className="textarea" style={{ minHeight: 60 }} value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
        </div>

        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          Estimated before tax: <strong style={{ color: 'var(--text)' }}>{money(estimate)}</strong> —
          tax and validity come from your quoting settings.
        </p>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className={'btn btn-primary' + (busy ? ' is-loading' : '')} onClick={submit}
            disabled={busy || !form.contact}>
            {busy ? 'Creating…' : 'Create quotation'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SettingsModal({ onClose }) {
  const [settings, setSettings] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/quotations/settings').then(r => {
      setSettings(r.data.settings);
      setWarehouses(r.data.warehouses || []);
    }).catch(() => toast.error('Could not load the settings'));
  }, []);

  const set = (field, value) => setSettings(s => ({ ...s, [field]: value }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.put('/quotations/settings', settings);
      setSettings(r.data.settings);
      toast.success('Quoting settings saved');
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save');
    } finally { setBusy(false); }
  };

  if (!settings) return null;

  return (
    <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-title">Quoting settings</div>
        {error && <div className="alert alert-danger" role="alert">{error}</div>}

        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 14 }}>
          <input type="checkbox" checked={settings.enabled} onChange={e => set('enabled', e.target.checked)} />
          <span>
            <strong style={{ fontSize: 13 }}>Let customers request quotes on WhatsApp</strong>
            <span className="field-help">
              A customer sends your keyword, lists what they want, and gets a PDF back. Off until
              your catalog is ready.
            </span>
          </span>
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label className="label">Keyword</label>
            <input className="input" value={settings.keyword}
              onChange={e => set('keyword', e.target.value.toUpperCase())} />
            <span className="field-help">What starts a request.</span>
          </div>
          <div className="form-group">
            <label className="label">Currency</label>
            <input className="input" value={settings.currency}
              onChange={e => set('currency', e.target.value.toUpperCase())} />
          </div>
          <div className="form-group">
            <label className="label">Default tax %</label>
            <input className="input" type="number" min="0" max="100" step="0.01" value={settings.tax_rate}
              onChange={e => set('tax_rate', e.target.value)} />
            <span className="field-help">Used when a product sets none.</span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label className="label">Valid for (days)</label>
            <input className="input" type="number" min="1" max="365" value={settings.validity_days}
              onChange={e => set('validity_days', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="label">Number prefix</label>
            <input className="input" value={settings.number_prefix}
              onChange={e => set('number_prefix', e.target.value.toUpperCase())} />
          </div>
          <div className="form-group">
            <label className="label">Quote from</label>
            <select className="select" value={settings.default_warehouse || ''}
              onChange={e => set('default_warehouse', e.target.value || null)}>
              <option value="">The default warehouse</option>
              {warehouses.map(w => <option key={w._id} value={w._id}>{w.name}</option>)}
            </select>
          </div>
        </div>

        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12 }}>
          <input type="checkbox" checked={settings.auto_send} onChange={e => set('auto_send', e.target.checked)} />
          <span>
            <strong style={{ fontSize: 13 }}>Send the quote straight away</strong>
            <span className="field-help">
              Off means a WhatsApp request is raised as "needs pricing" for someone to check and
              send by hand.
            </span>
          </span>
        </label>

        <div className="form-group">
          <label className="label">Business address on the PDF <span className="optional">(optional)</span></label>
          <input className="input" value={settings.business_address}
            onChange={e => set('business_address', e.target.value)} placeholder="12 Samora Machel Ave, Harare" />
        </div>

        <div className="form-group">
          <label className="label">Terms printed on every quote <span className="optional">(optional)</span></label>
          <textarea className="textarea" style={{ minHeight: 70 }} value={settings.terms}
            onChange={e => set('terms', e.target.value)}
            placeholder="Prices exclude delivery. Payment 50% deposit, balance on delivery." />
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className={'btn btn-primary' + (busy ? ' is-loading' : '')} onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      </div>
    </div>
  );
}
